import { useEffect, useMemo, useState } from 'react';
import { Link, useLocation, useSearchParams } from 'react-router-dom';
import { AsyncBoundary, Notice } from '@kollez/design-system';
import { OPPORTUNITY_TYPES, OPPORTUNITY_TYPE_LABELS, formatLPA, type OpportunityType } from '@kollez/shared';

import { useOpportunities, type OpportunityRow } from '@/lib/queries';

/**
 * Tenant-side opportunities list. Mounted at:
 *   /group/opportunities → cross-college view (group_admin, group_manager, …)
 *   /tpo/opportunities   → college-scoped view (tpo, assistant_tpo)
 *
 * Filters, sort, and pagination all round-trip through the server. Every
 * knob maps 1:1 to what `listOpportunitiesHandler` already validates via
 * Zod (page, limit, q, status, partner, type, scope, workMode, ctcMin,
 * ctcMax, postedAfter, postedBefore, sortBy, sortDir), so a browser reload
 * with URL params rehydrates the exact query. No API changes required.
 *
 * Search is debounced 300ms so keystrokes don't hammer the endpoint.
 */

const PAGE_LIMIT = 25;

type StatusFilter = 'Active' | 'Paused' | 'Draft' | 'Closed' | '';
type SortBy = 'createdAt' | 'title' | 'applicationDeadline' | 'compensation.ctc.max';
type SortDir = 'asc' | 'desc';
type WorkMode = 'onsite' | 'remote' | 'hybrid' | '';

const SORT_OPTIONS: Array<{ key: SortBy; label: string }> = [
  { key: 'createdAt', label: 'Newest' },
  { key: 'title', label: 'Title' },
  { key: 'applicationDeadline', label: 'Deadline' },
  { key: 'compensation.ctc.max', label: 'Compensation' },
];

function statusBadge(status: OpportunityRow['status']): { label: string; tone: string } {
  if (status === 'Active') return { label: 'Active', tone: 'badge-success' };
  if (status === 'Paused') return { label: 'Paused', tone: 'badge-warning' };
  if (status === 'Draft') return { label: 'Draft', tone: '' };
  if (status === 'Closed') return { label: 'Closed', tone: 'badge-danger' };
  return { label: status, tone: '' };
}

function fmtCTC(o: OpportunityRow): string | null {
  const ctc = o.compensation?.ctc;
  if (ctc?.min != null && ctc?.max != null) {
    return `${formatLPA(ctc.min)} – ${formatLPA(ctc.max)}`;
  }
  if (ctc?.max != null) return `up to ${formatLPA(ctc.max)}`;
  if (ctc?.min != null) return `from ${formatLPA(ctc.min)}`;
  const stipend = o.compensation?.stipend;
  if (stipend?.amount != null) {
    return `₹${stipend.amount.toLocaleString('en-IN')}${stipend.unit === 'per_month' ? '/mo' : ''}`;
  }
  return null;
}

/**
 * Debounce a value change by `delay` ms. Keeps the input snappy locally
 * while the server-round-trip fires only once the user pauses typing.
 */
function useDebounced<T>(value: T, delay = 300): T {
  const [debounced, setDebounced] = useState(value);
  useEffect(() => {
    const t = setTimeout(() => setDebounced(value), delay);
    return () => clearTimeout(t);
  }, [value, delay]);
  return debounced;
}

export function TenantOpportunitiesPage() {
  const { pathname } = useLocation();
  const isGroup = pathname.startsWith('/group/');
  const [params, setParams] = useSearchParams();

  // Every filter is URL-backed so refresh, share-link, and back-button all
  // preserve state without React state syncing headaches.
  const type = (params.get('type') ?? '') as OpportunityType | '';
  const status = (params.get('status') ?? 'Active') as StatusFilter;
  const workMode = (params.get('workMode') ?? '') as WorkMode;
  const ctcMinRaw = params.get('ctcMin') ?? '';
  const sortBy = (params.get('sortBy') ?? 'createdAt') as SortBy;
  const sortDir = (params.get('sortDir') ?? 'desc') as SortDir;
  const page = Math.max(1, Number.parseInt(params.get('page') ?? '1', 10) || 1);

  // Search stays as local state; we push the debounced value into params.
  // Reading params on mount rehydrates it when the user came in from a link.
  const [qInput, setQInput] = useState(() => params.get('q') ?? '');
  const qDebounced = useDebounced(qInput, 300);
  useEffect(() => {
    const next = new URLSearchParams(params);
    if (qDebounced.trim()) next.set('q', qDebounced.trim());
    else next.delete('q');
    // Debounced search changes reset pagination — keep the same feel students get in /explore.
    next.delete('page');
    if (next.toString() !== params.toString()) setParams(next, { replace: true });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [qDebounced]);

  function setParam(key: string, value: string | undefined, opts?: { resetPage?: boolean }) {
    const next = new URLSearchParams(params);
    if (!value) next.delete(key);
    else next.set(key, value);
    if (opts?.resetPage) next.delete('page');
    setParams(next, { replace: true });
  }

  const ctcMin = Number.parseInt(ctcMinRaw, 10);
  const ctcMinValid = Number.isFinite(ctcMin) && ctcMin >= 0;

  const { data, isLoading, isFetching, error, refetch } = useOpportunities({
    ...(type ? { type } : {}),
    ...(status ? { status } : {}),
    ...(workMode ? { workMode } : {}),
    ...(ctcMinValid ? { ctcMin } : {}),
    ...(qDebounced.trim() ? { q: qDebounced.trim() } : {}),
    sortBy,
    sortDir,
    page,
    limit: PAGE_LIMIT,
  });

  const rows = data?.opportunities ?? [];
  // The API response embeds `meta` with paging totals at the top level of the
  // envelope; useOpportunities unwraps `data`, so we peek at both shapes to
  // survive an envelope evolution without a controller-side deploy dependency.
  const meta = (data as { meta?: { page: number; limit: number; total: number } } | undefined)?.meta;
  const total = meta?.total ?? rows.length;
  const totalPages = Math.max(1, Math.ceil(total / PAGE_LIMIT));
  const startIdx = total === 0 ? 0 : (page - 1) * PAGE_LIMIT + 1;
  const endIdx = Math.min(page * PAGE_LIMIT, total);

  const summary = (data as { summary?: { byStatus?: Record<string, number> } } | undefined)?.summary;
  const countByType = useMemo(() => {
    const counts: Partial<Record<OpportunityType, number>> = {};
    for (const r of rows) counts[r.type as OpportunityType] = (counts[r.type as OpportunityType] ?? 0) + 1;
    return counts;
  }, [rows]);

  function toggleSort(nextKey: SortBy) {
    if (nextKey === sortBy) {
      const next = new URLSearchParams(params);
      next.set('sortDir', sortDir === 'asc' ? 'desc' : 'asc');
      setParams(next, { replace: true });
    } else {
      const next = new URLSearchParams(params);
      next.set('sortBy', nextKey);
      // Title sorts naturally ascending; everything else defaults to desc.
      next.set('sortDir', nextKey === 'title' ? 'asc' : 'desc');
      next.delete('page');
      setParams(next, { replace: true });
    }
  }

  function sortIndicator(key: SortBy): string {
    if (key !== sortBy) return '';
    return sortDir === 'asc' ? ' ↑' : ' ↓';
  }

  const hasNonDefaultFilters =
    type !== '' || status !== 'Active' || workMode !== '' || ctcMinRaw !== '' || qInput.trim() !== '' ||
    sortBy !== 'createdAt' || sortDir !== 'desc';

  function resetAll() {
    setQInput('');
    setParams(new URLSearchParams(), { replace: true });
  }

  return (
    <div className="stack">
      <div className="page-header">
        <div className="ph-text">
          <p className="eyebrow">Placement pipeline</p>
          <h1 className="h-page">Opportunities</h1>
          <p className="subhead">
            {isGroup
              ? 'Every posting from every partner across your colleges — placements, internships, workshops, hackathons, certifications, mentorships. Same feed your students see, at group scope.'
              : 'Every partner posting visible to your college. Filter by type; open one to see who has applied and where it sits in the funnel.'}
          </p>
        </div>
      </div>

      <div className="card">
        <div className="filterbar">
          <input
            className="input"
            placeholder="Search title, description, skills"
            value={qInput}
            onChange={(e) => setQInput(e.target.value)}
            style={{ minWidth: 240 }}
          />
          <select
            className="select"
            value={status}
            onChange={(e) => setParam('status', e.target.value, { resetPage: true })}
          >
            <option value="Active">Active</option>
            <option value="Paused">Paused</option>
            <option value="Draft">Draft</option>
            <option value="Closed">Closed</option>
            <option value="">Any status</option>
          </select>
          <select
            className="select"
            value={workMode}
            onChange={(e) => setParam('workMode', e.target.value, { resetPage: true })}
          >
            <option value="">Any location mode</option>
            <option value="onsite">On-site</option>
            <option value="remote">Remote</option>
            <option value="hybrid">Hybrid</option>
          </select>
          <input
            className="input"
            type="number"
            min={0}
            step={100000}
            placeholder="Min CTC (₹)"
            value={ctcMinRaw}
            onChange={(e) => setParam('ctcMin', e.target.value || undefined, { resetPage: true })}
            style={{ width: 140 }}
          />
          <select
            className="select"
            value={sortBy}
            onChange={(e) => {
              const next = new URLSearchParams(params);
              next.set('sortBy', e.target.value);
              next.delete('page');
              setParams(next, { replace: true });
            }}
          >
            {SORT_OPTIONS.map((o) => (
              <option key={o.key} value={o.key}>Sort: {o.label}</option>
            ))}
          </select>
          <button
            type="button"
            className="btn btn-outline btn-sm"
            aria-label={`Sort direction ${sortDir === 'asc' ? 'ascending' : 'descending'}`}
            onClick={() => {
              const next = new URLSearchParams(params);
              next.set('sortDir', sortDir === 'asc' ? 'desc' : 'asc');
              setParams(next, { replace: true });
            }}
          >
            {sortDir === 'asc' ? '↑' : '↓'}
          </button>
          {hasNonDefaultFilters && (
            <button type="button" className="btn btn-ghost btn-sm" onClick={resetAll}>
              Reset
            </button>
          )}
          <span className="fb-count">
            {isFetching ? '…' : total}
          </span>
        </div>

        {/* Type filter chips — one per canonical OPPORTUNITY_TYPES value.
            Counts show live results on the current page; the server already
            filters by `type` so we don't need a summary aggregate. */}
        <div className="chips-row" style={{ display: 'flex', gap: 8, flexWrap: 'wrap', padding: '8px 12px 0' }}>
          <button
            type="button"
            className={`chip ${type === '' ? 'chip-active' : ''}`}
            onClick={() => setParam('type', undefined, { resetPage: true })}
          >
            All types
          </button>
          {OPPORTUNITY_TYPES.map((t) => (
            <button
              key={t}
              type="button"
              className={`chip ${type === t ? 'chip-active' : ''}`}
              onClick={() => setParam('type', t, { resetPage: true })}
            >
              {OPPORTUNITY_TYPE_LABELS[t]}
              {countByType[t] != null && <span className="chip-count"> · {countByType[t]}</span>}
            </button>
          ))}
        </div>

        <AsyncBoundary
          kind="table"
          inCard
          loading={isLoading}
          error={error}
          empty={!isLoading && rows.length === 0}
          emptyTitle="No opportunities in scope"
          emptyHint={
            isGroup
              ? 'When one of your partners posts a hackathon, workshop, drive or internship, it lands here.'
              : 'Partners with an MoU covering your college will appear here once they post.'
          }
          onRetry={() => void refetch()}
        >
          <div className="tbl-wrap">
            <table className="tbl">
              <thead>
                <tr>
                  <th
                    onClick={() => toggleSort('title')}
                    style={{ cursor: 'pointer', userSelect: 'none' }}
                  >
                    Title{sortIndicator('title')}
                  </th>
                  <th className="hide-mobile">Partner</th>
                  <th>Type</th>
                  <th
                    className="hide-mobile"
                    onClick={() => toggleSort('compensation.ctc.max')}
                    style={{ cursor: 'pointer', userSelect: 'none' }}
                  >
                    Compensation{sortIndicator('compensation.ctc.max')}
                  </th>
                  <th
                    className="hide-mobile"
                    onClick={() => toggleSort('applicationDeadline')}
                    style={{ cursor: 'pointer', userSelect: 'none' }}
                  >
                    Deadline{sortIndicator('applicationDeadline')}
                  </th>
                  <th className="hide-mobile r">Applications</th>
                  <th>Status</th>
                </tr>
              </thead>
              <tbody>
                {rows.map((r) => {
                  const badge = statusBadge(r.status);
                  const ctc = fmtCTC(r);
                  const deadline = (r as unknown as { applicationDeadline?: string }).applicationDeadline;
                  return (
                    <tr key={r._id}>
                      <td>
                        <Link className="cell-title" to={`/opportunities/${r._id}`}>{r.title}</Link>
                        {r.locations && r.locations.length > 0 && (
                          <div className="cell-sub">
                            {r.locations.slice(0, 2).map((l) => l.city).filter(Boolean).join(', ')}
                            {r.locations.length > 2 && ` +${r.locations.length - 2}`}
                            {r.workMode && ` · ${r.workMode}`}
                          </div>
                        )}
                      </td>
                      <td className="cell-sub hide-mobile">{r.partner?.name ?? '—'}</td>
                      <td className="cell-sub">{OPPORTUNITY_TYPE_LABELS[r.type as OpportunityType] ?? r.type}</td>
                      <td className="cell-sub hide-mobile">{ctc ?? '—'}</td>
                      <td className="cell-sub hide-mobile">
                        {deadline
                          ? new Date(deadline).toLocaleDateString('en-IN', { day: '2-digit', month: 'short', year: 'numeric' })
                          : '—'}
                      </td>
                      <td className="r hide-mobile">{r.applicationCount ?? 0}</td>
                      <td><span className={`badge ${badge.tone}`}>{badge.label}</span></td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>

          {/* Pagination footer — hidden while the empty state renders. */}
          {rows.length > 0 && (
            <div
              className="flex items-center justify-between"
              style={{ padding: '10px 12px', borderTop: '1px solid rgb(var(--line-tertiary))' }}
            >
              <span className="cell-sub">
                {total > 0
                  ? `Showing ${startIdx.toLocaleString('en-IN')}–${endIdx.toLocaleString('en-IN')} of ${total.toLocaleString('en-IN')}`
                  : 'No results'}
                {summary?.byStatus && Object.keys(summary.byStatus).length > 1 && (
                  <span style={{ marginLeft: 12 }}>
                    (
                    {Object.entries(summary.byStatus)
                      .map(([s, n]) => `${s}: ${n}`)
                      .join(' · ')}
                    )
                  </span>
                )}
              </span>
              <div className="flex items-center gap-2">
                <button
                  type="button"
                  className="btn btn-outline btn-sm"
                  disabled={page <= 1 || isFetching}
                  onClick={() => setParam('page', String(Math.max(1, page - 1)))}
                >
                  ← Prev
                </button>
                <span className="cell-sub" style={{ minWidth: 90, textAlign: 'center' }}>
                  Page {page} of {totalPages}
                </span>
                <button
                  type="button"
                  className="btn btn-outline btn-sm"
                  disabled={page >= totalPages || isFetching}
                  onClick={() => setParam('page', String(Math.min(totalPages, page + 1)))}
                >
                  Next →
                </button>
              </div>
            </div>
          )}
        </AsyncBoundary>
      </div>

      <Notice tone="info">
        <b>Row click</b> opens the same detail page the student sees — useful for reviewing what an applicant is looking at before the drive kicks off.
      </Notice>
    </div>
  );
}
